import { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card, InputGroup } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetTestimonialByIdQuery,
  useUpdateTestimonialMutation,
} from "../../../redux/api/TestimonialApi/testimonialApi";
import { FaSave, FaArrowLeft, FaEdit, FaStar, FaImage } from "react-icons/fa";
import "../Services/Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const EditTestimonial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: testimonialData, isLoading: isFetching } =
    useGetTestimonialByIdQuery(id);
  const [updateTestimonial, { isLoading: isUpdating }] =
    useUpdateTestimonialMutation();

  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState("");

  useEffect(() => {
    if (testimonialData) {
      setExistingImage(testimonialData.client_image || "");
    }
  }, [testimonialData]);

  const validationSchema = Yup.object().shape({
    client_name: Yup.string().trim().required("Client name is required"),
    client_designation: Yup.string().trim().required("Designation is required"),
    client_rating: Yup.number().required("Rating is required"),
    client_review: Yup.string().trim().required("Review is required"),
    client_image: Yup.mixed().test(
      "required",
      "Client image is required",
      function (value) {
        if (existingImage) return true;
        return !!value;
      },
    ),
    display_order: Yup.number(),
    status: Yup.boolean(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      client_name: testimonialData?.client_name || "",
      client_designation: testimonialData?.client_designation || "",
      client_rating: testimonialData?.client_rating || 5,
      client_review: testimonialData?.client_review || "",
      display_order: testimonialData?.display_order || 0,
      status: testimonialData?.status ?? true,
      client_image: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      const data = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "client_image") {
          if (values[key]) {
            data.append(key, values[key]);
          }
        } else {
          data.append(key, values[key]);
        }
      });

      try {
        await updateTestimonial({ id, data }).unwrap();
        message.success("Testimonial updated successfully!");
        setTimeout(() => navigate("/admin/testimonial"), 2000);
      } catch (error) {
        message.error(error.data?.error || "Failed to update testimonial");
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      formik.setFieldValue("client_image", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (isFetching || isUpdating) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaEdit className="text-primary me-2" />
            Edit Testimonial
          </h2>
          <p className="text-muted mb-0">Modify the customer review details</p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/testimonial")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              Review Details
            </h5>
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Client Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="client_name"
                    {...formik.getFieldProps("client_name")}
                    isInvalid={
                      formik.touched.client_name && !!formik.errors.client_name
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.client_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Designation <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="client_designation"
                    {...formik.getFieldProps("client_designation")}
                    isInvalid={
                      formik.touched.client_designation &&
                      !!formik.errors.client_designation
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.client_designation}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Rating (1-5)</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-white">
                      <FaStar className="text-warning" />
                    </InputGroup.Text>
                    <Form.Select
                      name="client_rating"
                      {...formik.getFieldProps("client_rating")}
                    >
                      {[5, 4, 3, 2, 1].map((num) => (
                        <option key={num} value={num}>
                          {num} Stars
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    {...formik.getFieldProps("display_order")}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Client Image <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="custom-file-container">
                    <input
                      type="file"
                      id="testimonial-image-edit"
                      accept="image/*"
                      onChange={handleFileChange}
                      hidden
                    />
                    <label
                      htmlFor="testimonial-image-edit"
                      className={`d-flex align-items-center gap-2 border rounded-3 p-1 cursor-pointer bg-white shadow-sm ${formik.touched.client_image && formik.errors.client_image ? "border-danger" : ""}`}
                      style={{ minHeight: "45px" }}
                    >
                      <div className="btn btn-primary btn-sm rounded-pill px-3 ms-1">
                        Choose File
                      </div>
                      <span className="text-muted small text-truncate px-2 flex-grow-1">
                        {formik.values.client_image
                          ? formik.values.client_image.name
                          : existingImage
                            ? existingImage.split("/").pop()
                            : "No file chosen"}
                      </span>
                      <div className="pe-3 text-primary">
                        <FaImage />
                      </div>
                    </label>
                  </div>
                  {formik.touched.client_image &&
                    formik.errors.client_image && (
                      <div className="text-danger small mt-1">
                        {formik.errors.client_image}
                      </div>
                    )}

                  <div className="mt-3 d-flex gap-3">
                    {existingImage && !imagePreview && (
                      <div>
                        <small className="d-block text-muted mb-1">
                          Current Image:
                        </small>
                        <img
                          src={existingImage}
                          alt="Current"
                          className="rounded shadow-sm border p-1"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                    {imagePreview && (
                      <div>
                        <small className="d-block text-muted mb-1">
                          New Image Preview:
                        </small>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="rounded shadow-sm border p-1"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Review Message <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="client_review"
                    {...formik.getFieldProps("client_review")}
                    isInvalid={
                      formik.touched.client_review &&
                      !!formik.errors.client_review
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.client_review}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12} className="d-flex align-items-center">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="testimonial-status-edit"
                    label="Active / Published"
                    name="status"
                    checked={formik.values.status}
                    onChange={formik.handleChange}
                    className="fw-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/testimonial")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Update Testimonial
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default EditTestimonial;
